import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, InputSignal, output, ViewEncapsulation } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services';
import { EditRouteService } from '../../../../services/edit-route.service';
import { EditedPoint, EditedStatus, GetSuggestedPointsBody } from '../../../../interfaces/edit-route.interface';
import dayjs from 'dayjs';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MatBottomSheetAddNewPointOrReplacePointData} from 'src/app/interfaces/mat.interface';
import { MatDialogService } from 'src/app/services/mat-dialog.service';
import { SuggestedPointsService } from '../../../suggested-points/services/suggested-points.service';
import { SuggestedPointsComponent } from '../../../suggested-points/suggested-points.component';
import { DialogComponent } from 'src/app/shared/dialogs/dialog/dialog.component';
import { MatBottomSheetService } from 'src/app/services/mat-bottom-sheet.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { ReplaceButtonComponent } from "./components/replace-button/replace-button.component";
import { DeleteButtonComponent } from "./components/delete-button/delete-button.component";
import { RecoverButtonComponent } from "./components/recover-button/recover-button.component";

@Component({
  selector: 'app-actions-card',
  standalone: true,
  imports: [TranslateModule, ReplaceButtonComponent, DeleteButtonComponent, RecoverButtonComponent],
  templateUrl: './actions-card.component.html',
  styleUrls: ['./actions-card.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None
})
export class ActionsCardComponent {
  public goToFirstSlide = output();
  
  public point: InputSignal<EditedPoint> = input.required<EditedPoint>();
  public orderIndex: InputSignal<number> = input.required<number>();
  public previousEditedPoint: InputSignal<EditedPoint | null> = input<EditedPoint | null>(null);
  public nextEditedPoint: InputSignal<EditedPoint | null> = input<EditedPoint | null>(null);
  public isPointToAddNew: InputSignal<boolean> = input(false);
  public isPointToDelete: InputSignal<boolean> = input(false);
  public isPointToReplace: InputSignal<boolean> = input(false);
  
  private _translateService = inject(TranslateService);
  private _customRouteService = inject(CustomRouteService);
  private _editRouteService = inject(EditRouteService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _suggestedPointsService = inject(SuggestedPointsService);
  private _matBottomSheetService = inject(MatBottomSheetService);
  private _matDialogService = inject(MatDialogService);
  private _breakpointsService = inject(BreakpointsService);

  private _customRouteData = computed(() => this._customRouteService.customRouteData());
  private readonly _token = computed(() => this._customRouteService.token());
  private readonly _dayToShowIndex = computed(() => this._plannerDayStateService.dayToShowIndex());
  private _isMobile = computed(() => this._breakpointsService.isMobile());
  
  public handleReplacePointClick() {        
    const dayToString = dayjs(this._customRouteData().dailyRoutes[this._dayToShowIndex()].date).format('YYYY-MM-DD');

    console.log(this.nextEditedPoint());

    const body: GetSuggestedPointsBody = {
      token: this._token(),
      day: dayToString,
      startTime: dayjs(this.point().startDate).format('HH:mm'),
      duration: this.nextEditedPoint() && this.nextEditedPoint().id !== 0
        ? dayjs(this.nextEditedPoint().startDate).diff(dayjs(this.point().startDate), 'minute') - 15
        : dayjs('24:00', 'HH:mm').diff(dayjs(this.point().startDate), 'minute') - 15, // subtract 15 min due to transports
      previousPoint: this.previousEditedPoint() &&
        { 
          id: this.previousEditedPoint().id, 
          type: this.previousEditedPoint().type 
        },
      nextPoint: this.nextEditedPoint() &&
        { 
          id: this.nextEditedPoint().id, 
          type: this.nextEditedPoint().type 
        }
    }
    
    this._editRouteService.getSuggestedPoints(body).subscribe({
      next: async (response) => {
        this._suggestedPointsService.suggestedPoints.set(response.points);
        this._suggestedPointsService.interestList.set(response.categoryList);
        await this._openSuggestedPointsBottomSheet();
      },
      error: (error: HttpErrorResponse) => {
        console.error(`Error fetching suggested points: ${error.error.error}`);
        this._openNoSuggestedPointsErrorDialog();
      }
    })
  }

  private async _openSuggestedPointsBottomSheet(): Promise<void> {
    await this._matBottomSheetService.openMatBottomSheet<SuggestedPointsComponent, MatBottomSheetAddNewPointOrReplacePointData>({
      component: SuggestedPointsComponent,
      data: {
        title: this._translateService.instant('Modificar recurso'),
        dayToShowIndex: this._dayToShowIndex(),
        orderIndex: this.orderIndex(),
        status: EditedStatus.UPDATED,
        pointToBeReplaced: this.point(),
      },
      panelClass: this._isMobile() ? 'suggested-points-bottom-sheet--mobile' : 'suggested-points-bottom-sheet--desktop',
    });   
  }
  
  private _openNoSuggestedPointsErrorDialog(): void {
    const dialogRef = this._matDialogService.openMatDialog({
      component: DialogComponent,
      data: {
        iconName: 'alert',
        title: this._translateService.instant('No se han encontrado recursos sugeridos'),
        content: this._translateService.instant('No hemos encontrado recursos sugeridos en esta franja horaria'),
        primaryActionButtonText: this._translateService.instant('Aceptar'),
        handleClickPrimaryButton: () => {
          this._matDialogService.closeMatDialog(dialogRef);
        }
      }
    })
  }
  
  public handleDeletePointClick() {        
    const pointId = this.point().id;
    const pointType = this.point().type;
    const dayIndex = this._dayToShowIndex();

    this._editRouteService.deletePointToEditedCustomRouteData(pointId, pointType);
    
    console.log(`Point with ID ${pointId} deleted - day ${dayIndex + 1}`);
    console.log('Edited Custom Route Data To Send:', this._editRouteService.customRouteEditedDataToSend());
    
    this.goToFirstSlide.emit();
  }
  
  public handleRecoverPointClick() {
    const pointId = this.point().id;
    const pointType = this.point().type;
    const dayIndex = this._dayToShowIndex();

    this._editRouteService.recoverPointToEditedCustomRouteData(pointId, pointType, dayIndex);
    
    console.log(`Point with ID ${pointId} recovered - day ${dayIndex + 1}`);
    console.log('Edited Custom Route Data To Send:', this._editRouteService.customRouteEditedDataToSend());
    
    this.goToFirstSlide.emit();
  }
}
